import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ConversationContext, ContentApproval } from '../interfaces/ai-enhanced-supplier-data.interface';

export interface ConversationStartRequest {
  session_id: string;
  conversation_history?: ConversationMessage[];
}

export interface ConversationMessage {
  role: string;
  content: string;
  timestamp?: string;
}

export interface ConversationMessageRequest {
  session_id: string;
  user_message: string;
  conversation_history?: ConversationMessage[];
  include_current_message?: boolean;
}

export interface ConversationResponse {
  status: string;
  session_id: string;
  ai_response: string;
  extracted_data?: { [key: string]: any };
  conversation_phase: string;
  next_question?: string;
  confidence?: number;
  needs_clarification?: string[];
  updated_conversation_history?: ConversationMessage[];
}

export interface ConversationContextResponse {
  status: string;
  session_id: string;
  phase: string;
  collected_data: { [key: string]: any };
  conversation_history: Array<{ role: string; content: string; timestamp: string }>;
  business_type?: string;
  industry_focus: string[];
  required_fields: string[];
}

export interface ProcessingResult {
  success: boolean;
  extractedData: { [key: string]: any };
  nextQuestion: string;
  confidence: number;
  phase: string;
  needsClarification: string[];
  skipDetected?: boolean;
  changeDetected?: boolean;
  changeField?: string;
  error?: string;
}

// Enhanced Question interface with Step 2.2 capabilities
export interface Question {
  text: string;
  type: string;
  expectedFields: string[];
  priority: number;
  followUpHints: string[];
  // Enhanced with business intelligence
  questionType?: string;
  confidence?: number;
  businessContext?: {
    detectedBusinessType?: string;
    detectedIndustries?: string[];
    showExpertise?: boolean;
  };
}

export interface ExtractedData {
  fields: { [key: string]: any };
  confidence: number;
  businessIntelligence: {
    detectedBusinessType?: string;
    detectedIndustries?: string[];
    emergencyServices?: boolean;
    customWork?: boolean;
    qualityCertifications?: boolean;
  };
}

// New interfaces for Step 2.2 backend integration
export interface QuestionGenerationRequest {
  session_id: string;
}

export interface QuestionGenerationResponse {
  status: string;
  session_id: string;
  question: string;
  question_type: string;
  expected_fields: string[];
  follow_up_hints: string[];
  priority: number;
  success: boolean;
  error_message?: string;
  // Enhanced business context
  business_context?: {
    detected_business_type?: string;
    detected_industries?: string[];
    confidence_level?: number;
  };
}

export interface ContentPreviewRequest {
  session_id: string;
}

export interface ContentPreviewResponse {
  status: string;
  session_id: string;
  preview_content: {
    hero_headline: string;
    hero_subheadline: string;
    hero_description: string;
    about_preview: string;
    capabilities_preview: string[];
    contact_preview: string;
    seo_title: string;
    meta_description: string;
  };
  data_completeness: number;
  confidence_score: number;
  missing_fields: string[];
  business_insights: {
    detected_business_type?: string;
    industry_focus: string[];
    key_differentiators: string[];
  };
  last_updated: string;
  preview_sections: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AIConversationService {
  private apiUrl = `${environment.fastApiBaseUrl}/ai`;
  private currentSessionId = this.generateSessionId();
  
  // State management
  private conversationStateSubject = new BehaviorSubject<ConversationContextResponse | null>(null);
  public conversationState$ = this.conversationStateSubject.asObservable();
  
  private isProcessingSubject = new BehaviorSubject<boolean>(false);
  public isProcessing$ = this.isProcessingSubject.asObservable();

  private fallbackModeSubject = new BehaviorSubject<boolean>(false);
  public fallbackMode$ = this.fallbackModeSubject.asObservable();

  // New state for Step 2.2 enhancements
  private currentQuestionSubject = new BehaviorSubject<Question | null>(null);
  public currentQuestion$ = this.currentQuestionSubject.asObservable();

  private contentPreviewSubject = new BehaviorSubject<ContentPreviewResponse | null>(null);
  public contentPreview$ = this.contentPreviewSubject.asObservable();

  // Termination detection for chat completion
  private terminationDetectedSubject = new BehaviorSubject<boolean>(false);
  public terminationDetected$ = this.terminationDetectedSubject.asObservable();

  // AI content generation trigger
  private aiContentGenerationTriggerSubject = new BehaviorSubject<boolean>(false);
  public aiContentGenerationTrigger$ = this.aiContentGenerationTriggerSubject.asObservable();



  constructor(private http: HttpClient) {}

  /**
   * Enhanced processUserResponse with Step 2.2 backend integration
   * Phase 1 requirement: async processUserResponse(input: string): Promise<ProcessingResult>
   */
  async processUserResponse(input: string): Promise<ProcessingResult> {
    try {
      this.setProcessing(true);
      
      // Send message to backend for processing
      const response = await this.sendMessage(input).toPromise();
      
      if (!response || response.status !== 'success') {
        throw new Error('Failed to process user response');
      }
      
      // Update conversation state
      const context = await this.getConversationContext().toPromise();
      if (context) {
        this.updateConversationState(context);
      }

      // Check for skip detection
      const skipDetected = response.extracted_data && response.extracted_data['_skip_detected'];
      
      // Check for change detection
      const changeDetected = response.extracted_data && response.extracted_data['_change_detected'];
      const changeField = response.extracted_data && response.extracted_data['_change_field'];

      // Check if AI response indicates termination (all data collected)
      const isTerminationResponse = response.ai_response && 
        (response.ai_response.includes("Perfect! I've collected all the essential information") ||
         response.ai_response.includes("website is ready to be created and deployed") ||
         response.ai_response.includes("Your website is ready"));

      if (isTerminationResponse) {
        console.log('🏁 AI indicated termination - all data collected');
        
        // Update current question state to null to indicate no more questions
        this.currentQuestionSubject.next(null);
        
        // Emit termination signal
        this.terminationDetectedSubject.next(true);
        
        // Trigger AI content generation
        this.triggerAIContentGeneration();
        
        return {
          success: true,
          extractedData: response.extracted_data || {},
          nextQuestion: '',
          confidence: response.confidence || 1.0,
          phase: 'deployment',
          needsClarification: [],
          skipDetected: skipDetected || false,
          changeDetected: changeDetected || false,
          changeField: changeField || undefined
        };
      }

      // Use the question from the message response instead of making a redundant API call
      const nextQuestionText = response.next_question || response.ai_response || 'Could you tell me more about your business?';
      
      // Create question object for state management
      const nextQuestion: Question = {
        text: nextQuestionText,
        type: 'contextual',
        expectedFields: [],
        priority: 1,
        followUpHints: [],
        questionType: 'contextual',
        confidence: response.confidence || 0.5
      };
      
      // Update current question state
      this.currentQuestionSubject.next(nextQuestion);
      
      // Update content preview
      await this.updateContentPreview();
      
      return {
        success: true,
        extractedData: response.extracted_data || {},
        nextQuestion: nextQuestionText,
        confidence: response.confidence || 0,
        phase: response.conversation_phase,
        needsClarification: response.needs_clarification || [],
        skipDetected: skipDetected || false,
        changeDetected: changeDetected || false,
        changeField: changeField || undefined
      };
      
    } catch (error) {
      console.error('Error processing user response:', error);
      this.fallbackToManualFlow();
      
      return {
        success: false,
        extractedData: {},
        nextQuestion: 'Could you tell me more about your business?',
        confidence: 0,
        phase: 'manual_fallback',
        needsClarification: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    } finally {
      this.setProcessing(false);
    }
  }

  /**
   * Enhanced generateNextQuestion using Step 2.2 backend API
   * Phase 1 requirement: async generateNextQuestion(context: ConversationContext): Promise<Question>
   */
  async generateNextQuestion(context?: ConversationContext): Promise<Question> {
    try {
      this.setProcessing(true);
      
      // Use backend AI question generation from Step 2.2
      const backendQuestion = await this.generateNextQuestionFromBackend();
      
      // Update current question state
      this.currentQuestionSubject.next(backendQuestion);
      
      return backendQuestion;
      
    } catch (error) {
      console.error('Error generating next question:', error);
      
      // Fallback to basic question generation
      const fallbackQuestion = this.generateFallbackQuestion(context);
      this.currentQuestionSubject.next(fallbackQuestion);
      
      return fallbackQuestion;
    } finally {
      this.setProcessing(false);
    }
  }

  /**
   * New method: Generate next question using Step 2.2 backend API
   */
  private async generateNextQuestionFromBackend(): Promise<Question> {
    const request: QuestionGenerationRequest = {
      session_id: this.currentSessionId
    };

    const response = await this.http.post<QuestionGenerationResponse>(
      `${this.apiUrl}/conversation/generate-question`, 
      request
    ).toPromise();

    if (!response || response.status !== 'success') {
      throw new Error('Failed to generate question from backend');
    }

    return {
      text: response.question,
      type: response.question_type,
      expectedFields: response.expected_fields,
      followUpHints: response.follow_up_hints,
      priority: response.priority,
      questionType: response.question_type,
      confidence: response.business_context?.confidence_level || 0.5,
      businessContext: {
        detectedBusinessType: response.business_context?.detected_business_type,
        detectedIndustries: response.business_context?.detected_industries || [],
        showExpertise: !!response.business_context?.detected_business_type
      }
    };
  }

  /**
   * New method: Get real-time content preview using Step 2.1 backend API
   */
  async getContentPreview(): Promise<ContentPreviewResponse | null> {
    try {
      const request: ContentPreviewRequest = {
        session_id: this.currentSessionId
      };

      const response = await this.http.post<ContentPreviewResponse>(
        `${this.apiUrl}/conversation/preview-content`, 
        request
      ).toPromise();

      if (response && response.status === 'success') {
        this.contentPreviewSubject.next(response);
        return response;
      }

      return null;
    } catch (error) {
      console.error('Error getting content preview:', error);
      return null;
    }
  }

  /**
   * New method: Update content preview in state
   */
  private async updateContentPreview(): Promise<void> {
    try {
      await this.getContentPreview();
    } catch (error) {
      console.warn('Failed to update content preview:', error);
    }
  }

  /**
   * Enhanced extractDataFromResponse with improved business intelligence
   * Phase 1 requirement: async extractDataFromResponse(response: string): Promise<ExtractedData>
   */
  async extractDataFromResponse(response: string): Promise<ExtractedData> {
    try {
      // Validate the response using backend
      const validationResult = await this.validateData({ userResponse: response }).toPromise();
      
      // Extract enhanced business intelligence from response
      const businessIntelligence = this.extractEnhancedBusinessIntelligence(response);
      
      return {
        fields: validationResult.validated_data || {},
        confidence: this.calculateEnhancedConfidence(response, businessIntelligence),
        businessIntelligence
      };
      
    } catch (error) {
      console.error('Error extracting data from response:', error);
      
      return {
        fields: { userResponse: response },
        confidence: 0.5,
        businessIntelligence: {
          detectedIndustries: [],
          emergencyServices: false,
          customWork: false,
          qualityCertifications: false
        }
      };
    }
  }

  /**
   * Enhanced business intelligence extraction with Step 2.2 logic
   */
  private extractEnhancedBusinessIntelligence(response: string): any {
    const intelligence: any = {};
    const lowerResponse = response.toLowerCase();
    
    // Enhanced manufacturing type detection (matching backend logic)
    const manufacturingTypes: { [key: string]: string[] } = {
      'CNC Machining': ['cnc', 'machining', 'milling', 'turning', 'lathe'],
      'Welding': ['welding', 'weld', 'fabrication', 'metal joining'],
      'Sheet Metal': ['sheet metal', 'stamping', 'forming', 'press brake'],
      'Injection Molding': ['injection molding', 'plastic injection', 'injection', 'molding'],
      'Casting': ['casting', 'foundry', 'metal casting', 'sand casting', 'investment casting'],
      '3D Printing': ['3d printing', '3d print', 'additive', 'prototyping', 'additive manufacturing'],
      'Assembly': ['assembly', 'assembly line', 'production line', 'assembly services']
    };
    
    // Detect business type with priority order
    for (const [businessType, keywords] of Object.entries(manufacturingTypes)) {
      for (const keyword of keywords) {
        if (lowerResponse.includes(keyword)) {
          intelligence.detectedBusinessType = businessType;
          break;
        }
      }
      if (intelligence.detectedBusinessType) break;
    }
    
    // Enhanced industry detection
    const industries: string[] = [];
    const industryKeywords = {
      'Aerospace': ['aerospace', 'aviation', 'aircraft', 'space', 'satellite'],
      'Automotive': ['automotive', 'car', 'vehicle', 'truck', 'auto'],
      'Medical': ['medical', 'healthcare', 'pharmaceutical', 'biomedical', 'surgical'],
      'Energy': ['energy', 'oil', 'gas', 'renewable', 'solar', 'wind'],
      'Defense': ['defense', 'military', 'weapon', 'armor', 'tactical'],
      'Industrial': ['industrial', 'machinery', 'equipment', 'tools']
    };
    
    for (const [industry, keywords] of Object.entries(industryKeywords)) {
      if (keywords.some(keyword => lowerResponse.includes(keyword))) {
        industries.push(industry);
      }
    }
    intelligence.detectedIndustries = industries;
    
    // Enhanced service characteristics
    intelligence.emergencyServices = /\b(emergency|urgent|rush|24\/7|same\s+day)\b/.test(lowerResponse);
    intelligence.customWork = /\b(custom|prototype|one-off|specialized|bespoke|tailored)\b/.test(lowerResponse);
    intelligence.qualityCertifications = /\b(iso|certified|quality|as9100|iatf|nadcap)\b/.test(lowerResponse);
    
    return intelligence;
  }

  /**
   * Enhanced confidence calculation
   */
  private calculateEnhancedConfidence(response: string, businessIntelligence: any): number {
    let confidence = 0.5; // Base confidence
    
    // Response length and detail
    if (response.length > 50) confidence += 0.1;
    if (response.length > 100) confidence += 0.1;
    if (response.length > 200) confidence += 0.1;
    
    // Business intelligence factors
    if (businessIntelligence.detectedBusinessType) confidence += 0.15;
    if (businessIntelligence.detectedIndustries.length > 0) confidence += 0.1;
    if (businessIntelligence.qualityCertifications) confidence += 0.1;
    
    // Manufacturing terminology
    const manufacturingTerms = ['manufacturing', 'machining', 'welding', 'assembly', 'quality', 'precision', 'tolerance', 'capability'];
    const foundTerms = manufacturingTerms.filter(term => response.toLowerCase().includes(term));
    confidence += foundTerms.length * 0.03;
    
    return Math.min(confidence, 0.95);
  }

  /**
   * Generate fallback question when backend fails
   */
  private generateFallbackQuestion(context?: ConversationContext): Question {
    if (!context || !context.collected_data) {
      return {
        text: 'What is your company name?',
        type: 'company_identity',
        expectedFields: ['companyName'],
        priority: 1,
        followUpHints: ['Official business name', 'DBA name'],
        questionType: 'fallback_identity'
      };
    }

    const missingFields = this.analyzeMissingFields(context);
    
    if (missingFields.includes('companyName')) {
      return {
        text: 'What is your company name?',
        type: 'company_identity',
        expectedFields: ['companyName'],
        priority: 1,
        followUpHints: ['Official business name', 'DBA name'],
        questionType: 'fallback_identity'
      };
    }
    
    if (missingFields.includes('contactEmail')) {
      return {
        text: 'What is your business email address?',
        type: 'contact_info',
        expectedFields: ['contactEmail'],
        priority: 2,
        followUpHints: ['Primary business email'],
        questionType: 'fallback_contact'
      };
    }
    
    return {
      text: 'Could you tell me more about your business?',
      type: 'general',
      expectedFields: ['aboutContent'],
      priority: 3,
      followUpHints: ['Services', 'Experience', 'Specialization'],
      questionType: 'fallback_general'
    };
  }

  /**
   * Fallback to manual flow when AI fails
   * Phase 1 requirement: private fallbackToManualFlow(): void
   */
  private fallbackToManualFlow(): void {
    console.warn('AI conversation failed, falling back to manual flow');
    
    this.fallbackModeSubject.next(true);
    this.setProcessing(false);
    
    // Reset conversation state
    this.conversationStateSubject.next(null);
    this.currentQuestionSubject.next(null);
    
    console.info('Manual flow activated - using traditional question sequence');
  }

  /**
   * Check if currently in fallback mode
   */
  isInFallbackMode(): boolean {
    return this.fallbackModeSubject.value;
  }

  /**
   * Reset fallback mode (for testing or recovery)
   */
  resetFallbackMode(): void {
    this.fallbackModeSubject.next(false);
  }

  /**
   * Get current question from state
   */
  getCurrentQuestion(): Question | null {
    return this.currentQuestionSubject.value;
  }

  /**
   * Get current content preview from state
   */
  getCurrentContentPreview(): ContentPreviewResponse | null {
    return this.contentPreviewSubject.value;
  }

  // Existing methods (keeping all the original methods)

  /**
   * Start a new AI conversation session with optional conversation history
   */
  startConversation(existingHistory?: ConversationMessage[]): Observable<ConversationResponse> {
    const request: ConversationStartRequest = {
      session_id: this.currentSessionId,
      conversation_history: existingHistory || []
    };
    
    this.isProcessingSubject.next(true);
    return this.http.post<ConversationResponse>(`${this.apiUrl}/conversation/start`, request)
      .pipe(
        map(response => {
          // Update local conversation history if provided in response
          if (response.updated_conversation_history) {
            const newState: ConversationContextResponse = {
              status: response.status,
              session_id: response.session_id,
              phase: response.conversation_phase,
              collected_data: response.extracted_data || {},
              conversation_history: response.updated_conversation_history.map((msg: ConversationMessage) => ({
                role: msg.role,
                content: msg.content,
                timestamp: msg.timestamp || new Date().toISOString()
              })),
              business_type: undefined,
              industry_focus: [],
              required_fields: []
            };
            this.conversationStateSubject.next(newState);
          }
          return response;
        }),
        catchError(error => {
          this.fallbackToManualFlow();
          throw error;
        })
      );
  }

  /**
   * Send a message in the current conversation with full conversation history
   */
  sendMessage(userMessage: string, includeHistory: boolean = true): Observable<ConversationResponse> {
    const currentState = this.conversationStateSubject.value;
    
    let conversationHistory: ConversationMessage[] = [];
    if (includeHistory && currentState?.conversation_history) {
      conversationHistory = currentState.conversation_history.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      }));
    }
    
    const request: ConversationMessageRequest = {
      session_id: this.currentSessionId,
      user_message: userMessage,
      conversation_history: conversationHistory,
      include_current_message: true
    };
    
    this.isProcessingSubject.next(true);
    return this.http.post<ConversationResponse>(`${this.apiUrl}/conversation/message`, request)
      .pipe(
        map(response => {
          // Handle malformed responses
          if (typeof response === 'string') {
            console.error('Received string response instead of JSON');
            return {
              status: 'error',
              session_id: this.currentSessionId,
              ai_response: 'Could you tell me more about your business?',
              conversation_phase: 'error'
            };
          }
          
          // Update local conversation history if provided in response
          if (response.updated_conversation_history && currentState) {
            const updatedState: ConversationContextResponse = {
              ...currentState,
              conversation_history: response.updated_conversation_history.map((msg: ConversationMessage) => ({
                role: msg.role,
                content: msg.content,
                timestamp: msg.timestamp || new Date().toISOString()
              }))
            };
            this.conversationStateSubject.next(updatedState);
          }
          
          return response;
        }),
        catchError(error => {
          this.fallbackToManualFlow();
          throw error;
        })
      );
  }

  /**
   * Get current conversation context
   */
  getConversationContext(): Observable<ConversationContextResponse> {
    return this.http.get<ConversationContextResponse>(`${this.apiUrl}/conversation/context/${this.currentSessionId}`)
      .pipe(
        catchError(error => {
          this.fallbackToManualFlow();
          throw error;
        })
      );
  }

  /**
   * Validate extracted data
   */
  validateData(data: { [key: string]: any }): Observable<any> {
    return this.http.post(`${this.apiUrl}/conversation/validate`, data)
      .pipe(
        catchError(error => {
          console.warn('Data validation failed, continuing with manual validation');
          return new Observable(observer => {
            observer.next({ validated_data: data });
            observer.complete();
          });
        })
      );
  }

  /**
   * Check if conversation service is healthy
   */
  checkHealth(): Observable<any> {
    return this.http.get(`${this.apiUrl}/conversation/health`);
  }

  /**
   * Update conversation state
   */
  updateConversationState(state: ConversationContextResponse): void {
    this.conversationStateSubject.next(state);
  }

  setProcessing(processing: boolean): void {
    this.isProcessingSubject.next(processing);
  }

  getCurrentSessionId(): string {
    return this.currentSessionId;
  }

  resetConversation(): void {
    this.currentSessionId = this.generateSessionId();
    this.conversationStateSubject.next(null);
    this.currentQuestionSubject.next(null);
    this.contentPreviewSubject.next(null);
    this.fallbackModeSubject.next(false);
    this.terminationDetectedSubject.next(false);
    this.setProcessing(false);
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private analyzeMissingFields(context: ConversationContextResponse | ConversationContext): string[] {
    const required = context.required_fields || [];
    const collected = Object.keys(context.collected_data || {});
    return required.filter(field => !collected.includes(field));
  }

  /**
   * Trigger AI content generation
   */
  triggerAIContentGeneration(): void {
    this.aiContentGenerationTriggerSubject.next(true);
  }


}